'use client';

import Box from '../../../atoms/Box/Box';
import Button from '../../../atoms/Button/Button';
import Pill from '../../../atoms/Pill/Pill';
import type { FilterState, TableColumn, FilterValue } from '../types';
import styles from './FilterPills.module.scss';

type FilterPillData = {
  columnKey: string;
  columnTitle: string;
  value: FilterValue;
  displayValue: string;
};

export type FilterPillsProps = {
  /** Current filter state */
  filterState: FilterState;
  /** Table columns configuration */
  columns: TableColumn[];
  /** Callback when filters change */
  onFilterChange: (filters: FilterState) => void;
  /** Theme variant */
  theme?: 'light' | 'dark';
  /** Whether to show the Clear All button */
  showClearAll?: boolean;
  /** Data test ID prefix */
  dataTestId?: string;
  /** i18n labels for localization */
  labels?: {
    /** Label for "Clear All" button */
    clearAll?: string;
  };
};

/**
 * Looks up the display label for a filter value from column options
 */
const getDisplayLabel = (value: string, column: TableColumn): string => {
  const filterOptions = column.filter?.options;
  if (!filterOptions) return value;

  const option = filterOptions.find((opt) => opt.value === value);
  return option?.label || value;
};

/**
 * Generates filter pill data from filter state
 */
const getFilterPills = (filterState: FilterState, columns: TableColumn[]): FilterPillData[] => {
  const pills: FilterPillData[] = [];

  Object.entries(filterState).forEach(([columnKey, value]) => {
    if (value === null || value === undefined) return;

    const column = columns.find((col) => col.key === columnKey);
    if (!column) return;

    let displayValue = '';

    if (typeof value === 'string') {
      // Look up label from filter options
      displayValue = getDisplayLabel(value, column);
    } else if (Array.isArray(value)) {
      // Multiselect - show count or values with labels
      if (value.length === 0) return;
      if (value.length > 2) {
        displayValue = `${value.length} selected`;
      } else {
        // Look up labels for each value
        displayValue = value.map((v) => getDisplayLabel(v, column)).join(', ');
      }
    } else if (typeof value === 'object') {
      // Range filter (number or date)
      const { from, to } = value;
      if (from !== undefined && to !== undefined) {
        displayValue = `${from} - ${to}`;
      } else if (from !== undefined) {
        displayValue = `≥ ${from}`;
      } else if (to !== undefined) {
        displayValue = `≤ ${to}`;
      } else {
        return; // No actual filter value
      }
    }

    if (displayValue) {
      pills.push({
        columnKey,
        columnTitle: column.title,
        value,
        displayValue,
      });
    }
  });

  return pills;
};

/**
 * Displays active filter pills with option to remove individual filters or clear all
 * @component
 */
const FilterPills = ({
  filterState,
  columns,
  onFilterChange,
  theme = 'dark',
  showClearAll = true,
  dataTestId = 'filter-pills',
  labels,
}: FilterPillsProps) => {
  const pills = getFilterPills(filterState, columns);

  if (pills.length === 0) return null;

  const handleRemoveFilter = (columnKey: string) => {
    const newFilterState = { ...filterState };
    delete newFilterState[columnKey];
    onFilterChange(newFilterState);
  };

  const handleClearAll = () => {
    onFilterChange({});
  };

  return (
    <Box className={styles.filterPillsRow} data-testid={dataTestId}>
      {showClearAll && (
        <Button
          size="small"
          label={labels?.clearAll ?? 'Clear All'}
          gaComponent="filter-pills-clear-all"
          dataTestId={`${dataTestId}-clear-all`}
          handleClick={handleClearAll}
          theme={theme}
          type="tertiary"
          color="neutral"
        />
      )}
      <Box className={styles.filterPillsContainer}>
        {pills.map((pill) => (
          <Pill
            key={pill.columnKey}
            label={`${pill.columnTitle}: ${pill.displayValue}`}
            theme={theme}
            color="neutral"
            type="secondary"
            size="small"
            icon={{
              category: 'global',
              name: 'close',
              size: 12,
            }}
            handleClick={() => handleRemoveFilter(pill.columnKey)}
            dataTestId={`${dataTestId}-${pill.columnKey}`}
          />
        ))}
      </Box>
    </Box>
  );
};

FilterPills.displayName = 'FilterPills';

export default FilterPills;
